import { useEffect } from 'react'

import { reconcileDisplayPersistence } from '../auth/firebase-auth.ts'
import { useViewerAccess } from '../hooks/use-viewer-access.ts'
import { ApiRequestError } from '../lib/api-error.ts'
import { useSession } from '../session.tsx'

/**
 * Whether the backend refused this viewer.
 *
 * A 401 or 403 clears a restored display session even when the query still
 * holds an earlier answer. A missing answer, a timeout, a rate limit, or a
 * server error does not.
 *
 * @param error - The query error, when the check failed.
 * @returns Whether the status is 401 or 403.
 */
function accessRefused(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    (error.status === 401 || error.status === 403)
  )
}

/**
 * DisplayAccountGate — move a confirmed display account onto persistent
 * storage, and clear that storage for anyone else.
 *
 * The decision is the backend's `displayAccount` flag (ADR-0019). A check
 * that has not answered yet waits. The frontend signs out when the backend
 * says the viewer is not a display account, answers 401 or 403, or answers a
 * body it cannot read. A check that gets no answer or a server error keeps
 * the session. A later refresh that fails for any other reason keeps the last
 * answer: the query still holds it, and a blip must not sign a kiosk out.
 *
 * @returns Nothing.
 */
export function DisplayAccountGate(): null {
  const { userName } = useSession()
  const access = useViewerAccess(userName)

  useEffect(() => {
    if (access.isError && accessRefused(access.error)) {
      void reconcileDisplayPersistence('failed')
      return
    }
    if (access.data !== undefined) {
      const flag = access.data.displayAccount
      void reconcileDisplayPersistence(
        typeof flag === 'boolean' ? flag : 'failed'
      )
      return
    }
    if (!access.isError || access.error instanceof ApiRequestError) return
    void reconcileDisplayPersistence('failed')
  }, [access.data, access.isError, access.error])

  return null
}
